import React, {useState} from "react";
import ModalHeader from "@/customComponents/uiComponents/modal/ModalHeader.jsx";
import {PickupWhyContent} from "./smartPickupWhy.jsx";
import {isNearAirport, getWaitingBucket, getAirportBufferMinutes, getWaitingItemIcon} from "@/utils/airports";
import {storage, STORAGE_KEYS} from "@/utils/storage";

/**
 * "Typical Exit Time After Landing" info popup — opened from the ⓘ next to that
 * title on travelingDateTime (fromAirport only), as a stacked modal step at every
 * width (openModalStep), same as smartPickupWhy.
 *
 * Two tabs (International / Domestic, the CMR sliding-pill bar) over the SAME body as
 * "Why this pickup time?" — PickupWhyContent — fed from the pickup airport's
 * from_airport_{international,local} waiting_times bucket.
 *
 * travelingDateTime hands over the landing time picked on the wheel through this
 * module-level store right before opening the step (for the "…for your 3:45 AM
 * arrival" note).
 */
let landingTimeLabel = "";

export const setExitTimeInfoLanding = (label) => {
    landingTimeLabel = label || "";
};

const TABS = [
    {key: "international", label: "International"},
    {key: "domestic", label: "Domestic"},
];

// "Toronto Pearson International Airport" → "Toronto Pearson"
const cleanAirportName = (name) =>
    (name || "").replace(/\s*International Airport$/i, "").replace(/\s*Airport$/i, "").trim();

const formatDurationHM = (minutes) => {
    const total = Math.max(0, Math.round(Number(minutes) || 0));
    const h = Math.floor(total / 60);
    const m = total % 60;
    if (h === 0) return `${m} min`;
    if (m === 0) return `${h} hr`;
    return `${h} hr ${m} min`;
};

// Same shape smartPickupResult builds for a fromAirport whyData: the bucket's items
// ARE the rows, and the total is the cushion the recommendation would use.
const buildExitData = (international) => {
    const destinations = storage.get(STORAGE_KEYS.DESTINATIONS, []);
    const firstDest = destinations[0];
    const airport = firstDest?.lat && firstDest?.lng ? isNearAirport(firstDest.lat, firstDest.lng) : null;
    const opts = {fromAirport: true, international};
    const bucket = getWaitingBucket(airport, opts);
    const buffer = getAirportBufferMinutes(airport, opts);

    return {
        isFromAirport: true,
        note: "Your driver monitors your flight and adjusts pickup time automatically.",
        airportLabel: cleanAirportName(airport?.name) || "the airport",
        flightTimeLabel: landingTimeLabel,
        rows: (bucket?.items ?? []).map((item) => ({
            icon: getWaitingItemIcon(item.name),
            label: item.name,
            value: `~${formatDurationHM(item.minutes)}`,
        })),
        totalLabel: `~${formatDurationHM(buffer)}`,
    };
};

export function ExitTimeInfo(props) {
    // Opens on the flight type the passenger last chose, else International.
    const [tab, setTab] = useState(
        () => storage.get(STORAGE_KEYS.SMART_PICKUP_FLIGHT_TYPE) === "Domestic" ? "domestic" : "international"
    );
    const data = buildExitData(tab === "international");

    // ✕ pops the overlay back to travelingDateTime.
    return (
        <>
            <ModalHeader
                title=""
                extraClasses=""
                totalSteps={0}
                cancelButton={true}
                cancelButtonType="icon"
                backStep={null}
                showBackButton={false}
                setStep={props.setStep}
                closeModal={props.closeModal}
            />
            <PickupWhyContent
                data={data}
                title="Typical Exit Time After Landing"
                onLiability={() => props.setStep("liabilityDisclaimer")}
                onFindingDriver={() => props.setStep("findingDriver")}
            >
                <div className="hopn-cmr hopn-exit-time-info">
                    <div className="hopn-cmr-tabs" role="tablist">
                        <span
                            className="hopn-cmr-tab-indicator"
                            style={{transform: `translateX(${TABS.findIndex((t) => t.key === tab) * 100}%)`}}
                            aria-hidden="true"
                        />
                        {TABS.map((t) => (
                            <button
                                key={t.key}
                                type="button"
                                role="tab"
                                aria-selected={tab === t.key}
                                className={`hopn-cmr-tab ${tab === t.key ? "active" : ""}`}
                                onClick={() => setTab(t.key)}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                </div>
            </PickupWhyContent>
        </>
    );
}
